import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { openDB } from 'idb';
import { BehaviorSubject, defer, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthService } from '../auth/auth.service';

type Action = { type: 'GET_INITIAL_STATE' };

export type OfflineDataState = {
  apiCallsCompletionInformation: {
    [key in 'registration' | 'information' | 'options' | 'flags' | 'ratings' | 'gallery']: {
      completionPercentage: number;
      loading: boolean;
    };
  };
  vehicleDetails: {
    vehicleItemCode: string;
    vehicleFrontImage: string | null;
  };
  vin: string;
  loading: boolean;
  completionPercentage: number;
  isCarLocked: boolean;
};

@Injectable({
  providedIn: 'root'
})
export class SyncPwaDataService {
  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _authenticationService: AuthService
  ) {}

  // localDataState: Observable<TState>;

  private readonly _state = new BehaviorSubject<OfflineDataState[]>([]);

  public state$ = this._state.asObservable();

  private readonly _isCurrentlyPushing = new BehaviorSubject(false);
  public isCurrentlyPushing$ = this._isCurrentlyPushing.asObservable();

  private async _getDatabase() {
    return await openDB('vam-pwa', 1);
  }

  public async reducer(action: Action) {
    switch (action.type) {
      case 'GET_INITIAL_STATE': {
        const db = await this._getDatabase();
        const tx = db.transaction('VAM-PWA-API-CALLS', 'readwrite');
        const store = tx.objectStore('VAM-PWA-API-CALLS');
        const data = await store.getAll();

        const userDataStoredInLocalStorage = localStorage.getItem('user');

        const parsedUserData = JSON.parse(userDataStoredInLocalStorage);

        const userId = parsedUserData?.['user']?.['user_data']?.['id'] ?? -1;

        const arrayToReturn: OfflineDataState[] = [];

        if (Array.isArray(data)) {
          for (let i = 0; i < data.length; i++) {
            const d = data[i];

            const relevantObject = d[userId];

            const isThereDataForThisUser = d[userId] !== undefined;
            if (isThereDataForThisUser) {
              const registrationApiExecutionCompletionPercentage =
                await this.getWhatPercentageOfApiCallHasBeenMadeUnderGroup({
                  vin: d?.['vin'],
                  group: 'registration'
                });

              const informationApiExecutionCompletionPercentage =
                await this.getWhatPercentageOfApiCallHasBeenMadeUnderGroup({
                  vin: d?.['vin'],
                  group: 'information'
                });

              const optionsApiExecutionCompletionPercentage =
                await this.getWhatPercentageOfApiCallHasBeenMadeUnderGroup({
                  vin: d?.['vin'],
                  group: 'options'
                });

              const flagsApiExecutionCompletionPercentage =
                await this.getWhatPercentageOfApiCallHasBeenMadeUnderGroup({
                  vin: d?.['vin'],
                  group: 'flags'
                });

              const ratingsApiExecutionCompletionPercentage =
                await this.getWhatPercentageOfApiCallHasBeenMadeUnderGroup({
                  vin: d?.['vin'],
                  group: 'ratings'
                });

              const galleryApiExecutionCompletionPercentage =
                await this.getWhatPercentageOfApiCallHasBeenMadeUnderGroup({
                  vin: d?.['vin'],
                  group: 'gallery'
                });

              let vehicleFrontImage: string | null = null;

              const possibleVehicleFrontImageId =
                d[userId]?.['localTempData']?.['vehicleGalleryData']?.['6063617178c62']?.[
                  'vehicle_gallery_data'
                ];

              if (possibleVehicleFrontImageId) {
                vehicleFrontImage = await this._getImageFromImageStore({
                  imageId: possibleVehicleFrontImageId,
                  get: 'thumbnail'
                });
              }

              let isThisCarLocked = true;

              if (
                localStorage.getItem('currently-unlocked-pwa-vin') &&
                localStorage.getItem('currently-unlocked-pwa-vin') === d?.['vin']
              ) {
                isThisCarLocked = false;
              }

              const obj: OfflineDataState = {
                vin: d?.['vin'],
                apiCallsCompletionInformation: {
                  registration: {
                    completionPercentage: registrationApiExecutionCompletionPercentage,
                    loading: false
                  },
                  information: {
                    completionPercentage: informationApiExecutionCompletionPercentage,
                    loading: false
                  },
                  options: {
                    completionPercentage: optionsApiExecutionCompletionPercentage,
                    loading: false
                  },
                  flags: {
                    completionPercentage: flagsApiExecutionCompletionPercentage,
                    loading: false
                  },
                  ratings: {
                    completionPercentage: ratingsApiExecutionCompletionPercentage,
                    loading: false
                  },
                  gallery: {
                    completionPercentage: galleryApiExecutionCompletionPercentage,
                    loading: false
                  }
                },
                vehicleDetails: {
                  vehicleItemCode:
                    d[userId]?.['localTempData']?.['vehicleInformationData']?.['vehicleItemCode'],
                  vehicleFrontImage: vehicleFrontImage
                },
                loading: false,
                completionPercentage:
                  registrationApiExecutionCompletionPercentage +
                  informationApiExecutionCompletionPercentage +
                  optionsApiExecutionCompletionPercentage +
                  flagsApiExecutionCompletionPercentage +
                  ratingsApiExecutionCompletionPercentage +
                  galleryApiExecutionCompletionPercentage,
                isCarLocked: isThisCarLocked
              };
              arrayToReturn.push(obj);
            }
          }
        }

        this._state.next([...arrayToReturn]);
        break;
        // return arrayToReturn;
      }
    }
  }

  public showCurrentStateNow() {
    // console.group('current state is');
    // console.log(this._state.getValue());
    // console.groupEnd();
  }

  public async deleteEntryForThisVin(vin: string) {
    const db = await this._getDatabase();
    const tx = db.transaction('VAM-PWA-API-CALLS', 'readwrite');
    const store = tx.objectStore('VAM-PWA-API-CALLS');
    const currentlyLoggedInUserId = await this._getCurrentlyLoggedInUserId();
    const oldObject = await store.get(vin);
    delete oldObject[currentlyLoggedInUserId];
    await store.put(oldObject);
    this.clearState(vin);
  }

  unlockThisVin(vin: string) {
    const indexOfTheStateObjectWhereVinMatched = this._state
      .getValue()
      .findIndex((item) => item.vin === vin);

    const updatedState: OfflineDataState[] = this._state.getValue().map((item, index) => {
      if (index === indexOfTheStateObjectWhereVinMatched) {
        return {
          ...this._state.getValue()[index],
          isCarLocked: false
        };
      } else
        return {
          ...this._state.getValue()[index],
          isCarLocked: true
        };
    });

    this._state.next(updatedState);
    localStorage.setItem('currently-unlocked-pwa-vin', vin);
  }

  lockThisVin(vin: string) {
    const indexOfTheStateObjectWhereVinMatched = this._state
      .getValue()
      .findIndex((item) => item.vin === vin);

    const updatedState: OfflineDataState[] = this._state.getValue().map((item, index) => {
      if (index === indexOfTheStateObjectWhereVinMatched) {
        return {
          ...this._state.getValue()[index],
          isCarLocked: true
        };
      } else return item;
    });

    this._state.next(updatedState);
    localStorage.setItem('currently-unlocked-pwa-vin', null);
  }

  async updateEntryWithExistingVehicleId(vin: string, vehicleId: string) {
    // do previous tasks here

    const db = await this._getDatabase();
    const tx = db.transaction('VAM-PWA-API-CALLS', 'readwrite');
    const store = tx.objectStore('VAM-PWA-API-CALLS');
    const currentlyLoggedInUserId = await this._getCurrentlyLoggedInUserId();
    const oldObject = await store.get(vin);

    const newObject = {
      ...oldObject,
      [currentlyLoggedInUserId]: {
        ...(oldObject?.[currentlyLoggedInUserId] ?? {}),
        apiCallsData: {
          ...(oldObject?.[currentlyLoggedInUserId]?.['apiCallsData'] ?? {}),
          registration: oldObject?.[currentlyLoggedInUserId]?.['apiCallsData']?.[
            'registration'
          ]?.map((item: any, index: any) => {
            if (item?.['apiData']?.['endpoint'] === '/car/vin-information') {
              return {
                ...oldObject?.[currentlyLoggedInUserId]?.['apiCallsData']?.['registration'][index],
                apiData: {
                  ...oldObject?.[currentlyLoggedInUserId]?.['apiCallsData']?.['registration'][
                    index
                  ]?.['apiData'],
                  body: {
                    ...oldObject?.[currentlyLoggedInUserId]?.['apiCallsData']?.['registration'][
                      index
                    ]?.['apiData']?.['body'],
                    vehicle_id: vehicleId
                  }
                }
              };
            } else {
              return item;
            }
          })
        }
      }
    };

    await store.put(newObject);
    await tx.done;

    // and then proceed with regular work here
    // await this.syncData(vin);
  }

  public async syncData(vin: string) {
    const currentSateData = this._state.getValue();

    this._isCurrentlyPushing.next(true);

    const indexOfTheStateObjectWhereVinMatched = this._state
      .getValue()
      .findIndex((item) => item.vin === vin);

    {
      const newArrayToBeStoredInState: OfflineDataState[] = this._state
        .getValue()
        .map((item, index) =>
          index === indexOfTheStateObjectWhereVinMatched
            ? {
                ...this._state.getValue()[index],
                loading: true
              }
            : item
        );

      this._state.next(newArrayToBeStoredInState);
    }

    await this._handleRegistrationApiData(vin);
    await this._handleInformationApiData(vin);
    await this._handleOptionsApiData(vin);
    await this._handleFlagsApiData(vin);
    await this._handleRatingsApiData(vin);
    await this._handleGalleryApiData(vin);

    {
      const newArrayToBeStoredInState: OfflineDataState[] = this._state
        .getValue()
        .map((item, index) =>
          index === indexOfTheStateObjectWhereVinMatched
            ? {
                ...this._state.getValue()[index],
                loading: false
              }
            : item
        );

      this._state.next(newArrayToBeStoredInState);
    }

    this._isCurrentlyPushing.next(false);

    await this._clearIndexedDBDataForThisVinAndThisUser(vin);
    this.clearState(vin);
  }

  public clearState(vin: string) {
    {
      const newArrayToBeStoredInState = this._state.getValue().filter((item) => item.vin !== vin);
      this._state.next(newArrayToBeStoredInState);
    }
  }

  public async _clearIndexedDBDataForThisVinAndThisUser(vin: string) {
    // this will delete the API calls
    const currentlyLoggedInUserId = this._getCurrentlyLoggedInUserId();
    {
      const db = await this._getDatabase();
      const tx = db.transaction('VAM-PWA-API-CALLS', 'readwrite');
      const store = tx.objectStore('VAM-PWA-API-CALLS');
      const data = await store.get(vin);

      delete data?.[currentlyLoggedInUserId];
      await store.put(data);
      await tx.done;
    }

    // this will delete the images
    {
      const db = await this._getDatabase();
      const tx = db.transaction('image-store', 'readwrite');
      const store = tx.objectStore('image-store');
      const data = await store.getAll();

      for (let i = 0; i < data.length; i++) {
        if (data[i]?.['userId'] === currentlyLoggedInUserId && data[i]?.['forVin'] === vin) {
          await store.delete(data[i]?.['id']);
        }
      }

      await tx.done;
    }
  }

  private _apiAlreadyExecutedLog(args: { endpoint: string; value: any }) {
    // console.group('API executed');
    // console.log(`${args.endpoint} has already been executed, skipping`);
    // console.log('relevant data for log purpose:', { value: args.value });
    // console.groupEnd();
  }

  private _apiHasNotExecutedLog(args: { endpoint: string; value: any }) {
    // console.group('API not executed');
    // console.log(`${args.endpoint} has not already been executed, proceeding`);
    // console.log('relevant data for log purpose:', { value: args.value });
    // console.groupEnd();
  }

  private async _getDataUrlFromStore(id: string) {
    const db = await this._getDatabase();
    const tx = db.transaction('image-store', 'readonly');
    const store = tx.objectStore('image-store');
    const data = await store.get(id);
    await tx.done;

    const fileMimeType = data?.['type'];

    const match = fileMimeType.match(/\/(.+)$/);

    let fileFormat = '';

    if (match) {
      fileFormat = match[1];
    }

    return { dataUrl: data?.['dataUrl'], format: fileFormat };
  }

  private async _getImageFromImageStore(args: {
    imageId: string;
    get: 'image' | 'whole' | 'thumbnail';
  }) {
    const db = await this._getDatabase();
    const tx = db.transaction('image-store', 'readonly');
    const store = tx.objectStore('image-store');
    const data = await store.get(args.imageId);
    await tx.done;
    switch (args.get) {
      case 'whole': {
        return data;
      }
      case 'image': {
        return data?.['dataUrl'];
      }
      case 'thumbnail': {
        return data?.['thumbnailDataUrl'];
      }
    }
  }

  private async _calculateCompletionPercentageOfEveryGroupExceptThisGroup(args: {
    vin: string;
    group: 'registration' | 'information' | 'options' | 'flags' | 'ratings' | 'gallery';
  }) {
    const allGroups = [
      'registration',
      'information',
      'options',
      'flags',
      'ratings',
      'gallery'
    ] as const;
    const allGroupsWithoutThisGroup = allGroups.filter((value) => value !== args.group);
    let summedValueWithExclusion = 0;

    for (let i = 0; i < allGroupsWithoutThisGroup.length; i++) {
      const currentSum = await this.getWhatPercentageOfApiCallHasBeenMadeUnderGroup({
        vin: args.vin,
        group: args.group
      });
      summedValueWithExclusion += currentSum;
    }
    return summedValueWithExclusion;
  }

  private async _updateUiState(args: {
    vin: string;
    group: 'registration' | 'information' | 'options' | 'flags' | 'ratings' | 'gallery';
  }) {
    const calculatedPercentage = await this.getWhatPercentageOfApiCallHasBeenMadeUnderGroup({
      vin: args.vin,
      group: args.group
    });
    const indexOfTheStateObjectWhereVinMatched = this._state
      .getValue()
      .findIndex((item) => item.vin === args.vin);

    const summedCompletionValueWithoutCurrentGroup =
      await this._calculateCompletionPercentageOfEveryGroupExceptThisGroup({
        vin: args.vin,
        group: args.group
      });

    const updatedState: OfflineDataState[] = this._state.getValue().map((item, index) => {
      if (index === indexOfTheStateObjectWhereVinMatched) {
        return {
          ...this._state.getValue()[index],
          apiCallsCompletionInformation: {
            ...this._state.getValue()[index]?.apiCallsCompletionInformation,
            [args.group]: {
              ...this._state.getValue()[index]?.apiCallsCompletionInformation?.[args.group],
              completionPercentage: calculatedPercentage
            }
          },
          // completionPercentage:
          //   this._state.getValue()[index].completionPercentage +
          //   (calculatedPercentage - this._state.getValue()[index].apiCallsCompletionInformation?.[`${args.group}`].completionPercentage)

          completionPercentage: summedCompletionValueWithoutCurrentGroup + calculatedPercentage
        };
      } else return item;
    });

    this._state.next(updatedState);
  }

  /**
   * This method will update the IndexedDB state for API calls if they are executed.\
   * For an API call object stored in the IndexedDB, it will change the `isExecuted` from false to true.
   */
  public async _synchronizeApiExecutionStatusWithIndexedDb(
    args:
      | {
          vin: string;
          userId: number;
          indexOfArrayWhereApiCallIsExecuted: number;
          group: 'registration' | 'information' | 'options' | 'flags' | 'gallery';
        }
      | {
          vin: string;
          userId: number;
          indexOfArrayWhereApiCallIsExecuted: number;
          group: 'ratings';
          subgroup:
            | 'exterior'
            | 'frame'
            | 'interior'
            | 'major_options'
            | 'mechanics'
            | 'generic'
            | 'grading';
        }
  ) {
    const vin = args.vin;
    const userId = args.userId;
    const group = args.group;
    const indexOfArrayWhereApiCallIsExecuted = args.indexOfArrayWhereApiCallIsExecuted;

    const db = await this._getDatabase();
    const tx = db.transaction('VAM-PWA-API-CALLS', 'readwrite');
    const store = tx.objectStore('VAM-PWA-API-CALLS');
    const relevantDataAgainstVin = await store.get(vin);

    if (group === 'ratings') {
      const subgroup = args.subgroup;

      const updatedStoreObject = {
        ...relevantDataAgainstVin,
        [userId]: {
          ...relevantDataAgainstVin[userId],
          apiCallsData: {
            ...relevantDataAgainstVin[userId]?.['apiCallsData'],
            [group]: {
              ...relevantDataAgainstVin[userId]?.['apiCallsData']?.[group],
              [subgroup]: relevantDataAgainstVin[userId]?.['apiCallsData']?.[group]?.[
                subgroup
              ]?.map((item: any, index: number) => {
                if (index === indexOfArrayWhereApiCallIsExecuted) {
                  return {
                    ...relevantDataAgainstVin[userId]?.['apiCallsData']?.[group]?.[subgroup][index],
                    apiData: {
                      ...relevantDataAgainstVin[userId]?.['apiCallsData']?.[group]?.[subgroup][
                        index
                      ]?.['apiData'],
                      isExecuted: true
                    }
                  };
                } else return item;
              })
            }
          }
        }
      };

      await store.put(updatedStoreObject);
      await tx.done;
    } else {
      const updatedStoreObject = {
        ...relevantDataAgainstVin,
        [userId]: {
          ...relevantDataAgainstVin[userId],
          apiCallsData: {
            ...relevantDataAgainstVin[userId]?.['apiCallsData'],
            [group]: relevantDataAgainstVin[userId]?.['apiCallsData']?.[group]?.map(
              (item: any, index: number) => {
                if (index === indexOfArrayWhereApiCallIsExecuted) {
                  return {
                    ...relevantDataAgainstVin[userId]?.['apiCallsData']?.[group]?.[index],
                    apiData: {
                      ...relevantDataAgainstVin[userId]?.['apiCallsData']?.[group]?.[index]?.[
                        'apiData'
                      ],
                      isExecuted: true
                    }
                  };
                } else return item;
              }
            )
          }
        }
      };

      await store.put(updatedStoreObject);
      await tx.done;
    }
  }

  private async _toggleLoaderForGroup(args: {
    vin: string;
    group: 'registration' | 'information' | 'options' | 'flags' | 'ratings' | 'gallery';
  }) {
    const group = args.group;
    const indexOfTheStateObjectWhereVinMatched = this._state
      .getValue()
      .findIndex((item) => item.vin === args.vin);

    {
      const newArrayToBeStoredInState: OfflineDataState[] = this._state
        .getValue()
        .map((item, index) =>
          index === indexOfTheStateObjectWhereVinMatched
            ? {
                ...this._state.getValue()[index],
                apiCallsCompletionInformation: {
                  ...this._state.getValue()[index].apiCallsCompletionInformation,
                  [group]: {
                    ...this._state.getValue()[index].apiCallsCompletionInformation?.[group],
                    loading:
                      !this._state.getValue()[index].apiCallsCompletionInformation?.[group].loading
                  }
                }
              }
            : item
        );

      this._state.next(newArrayToBeStoredInState);
    }
  }

  private async _handleRegistrationApiData(vin: string) {
    this._toggleLoaderForGroup({ vin, group: 'registration' });
    const db = await this._getDatabase();
    const tx = db.transaction('VAM-PWA-API-CALLS', 'readwrite');
    const store = tx.objectStore('VAM-PWA-API-CALLS');
    const relevantDataAgainstVin = await store.get(vin);
    await tx.done;

    const currentlyLoggedInUserId = this._getCurrentlyLoggedInUserId();
    const relevantDataAgainstVinForThisUser = relevantDataAgainstVin?.[currentlyLoggedInUserId];

    const registrationApiData =
      relevantDataAgainstVinForThisUser?.['apiCallsData']?.['registration'];

    if (Array.isArray(registrationApiData)) {
      for (let i = 0; i < registrationApiData.length; i++) {
        const d = registrationApiData[i];
        const endpoint = d?.['apiData']?.['endpoint'];

        switch (endpoint) {
          case '/car/vin-information': {
            // first need to check if this API call has already been executed
            if (d?.['apiData']?.['isExecuted'] === true) {
              this._apiAlreadyExecutedLog({ endpoint, value: d });
              continue;
            }
            this._apiHasNotExecutedLog({ endpoint, value: d });
            const url = `${environment.api}${endpoint}`;
            const token = this._authenticationService.userData.token;

            const res = await fetch(url, {
              body: JSON.stringify(d?.apiData?.['body']),
              method: d?.apiData?.['method'],
              headers: {
                'Content-Type': d?.apiData?.['contentType'],
                Authorization: `Bearer ${token}`
              }
            });

            if (!res.ok) {
              throw new Error(`Error: ${res.statusText}`);
            }

            const parsedResponse = await res.json();
            const generatedVehicleId = parsedResponse?.['vehicle_id'];

            // update the mapper object for vehicle ID (add the actual vehicle ID)
            {
              const db = await this._getDatabase();
              const tx = db.transaction('VAM-PWA-API-CALLS', 'readwrite');
              const store = tx.objectStore('VAM-PWA-API-CALLS');
              const relevantDataAgainstVin = await store.get(vin);

              const updatedStoreObject = {
                ...relevantDataAgainstVin,
                [currentlyLoggedInUserId]: {
                  ...relevantDataAgainstVin[currentlyLoggedInUserId],
                  mapperData: {
                    ...relevantDataAgainstVin[currentlyLoggedInUserId]?.['mapperData'],
                    vehicleId: {
                      ...relevantDataAgainstVin[currentlyLoggedInUserId]?.['mapperData']?.[
                        'vehicleId'
                      ],
                      remote: generatedVehicleId
                    }
                  }
                }
              };

              await store.put(updatedStoreObject);
              await tx.done;
            }

            // update the localStorage
            {
              localStorage.setItem('vehicleId', generatedVehicleId);
            }

            // update the indexeddb store to indicate that this API is executed i.e. change `isExecuted` to true

            await this._synchronizeApiExecutionStatusWithIndexedDb({
              vin: vin,
              userId: currentlyLoggedInUserId,
              indexOfArrayWhereApiCallIsExecuted: i,
              group: 'registration'
            });

            // now update the local UI state

            await this._updateUiState({ vin, group: 'registration' });

            break;
          }
          case '/car/vin-verification': {
            if (d?.['apiData']?.['isExecuted'] === true) {
              this._apiAlreadyExecutedLog({ endpoint, value: d });
              continue;
            }
            this._apiHasNotExecutedLog({ endpoint, value: d });

            {
              const url = `${environment.api}${endpoint}`;
              const token = this._authenticationService.userData.token;

              const db = await this._getDatabase();
              const tx = db.transaction('VAM-PWA-API-CALLS', 'readwrite');
              const store = tx.objectStore('VAM-PWA-API-CALLS');
              const relevantDataAgainstVin = await store.get(vin);
              await tx.done;

              const currentlyLoggedInUserId = this._getCurrentlyLoggedInUserId();
              const relevantDataAgainstVinForThisUser =
                relevantDataAgainstVin?.[currentlyLoggedInUserId];

              const fd = new FormData();
              const imageKeys = new Set([
                'estimara_image',
                'mvpi_image',
                'odo_image',
                'plate_image',
                'vin_image'
              ]);

              const arrayToIterate = Object.entries(d?.['apiData']?.['body'] ?? {});

              for (let i = 0; i < arrayToIterate.length; i++) {
                const key = arrayToIterate[i][0];
                const value = arrayToIterate[i][1];

                if (typeof key === 'string' && typeof value === 'string') {
                  if (imageKeys.has(key) && value !== '') {
                    const imageDataUrl = await this._getImageFromImageStore({
                      imageId: value,
                      get: 'image'
                    });
                    const file = await this.dataUrlToFile(imageDataUrl, `${key}.png`);
                    fd.append(key, file);
                  } else {
                    console.groupEnd();

                    if (key === 'vehicle_id') {
                      fd.append(
                        key,
                        relevantDataAgainstVinForThisUser?.['mapperData']?.['vehicleId']?.['remote']
                      );
                    } else {
                      fd.append(key, value);
                    }
                  }
                }
              }

              const res = await fetch(url, {
                body: fd,
                method: d?.apiData?.['method'],
                headers: {
                  Authorization: `Bearer ${token}`
                }
              });

              if (!res.ok) {
                throw new Error(`Error: ${res.statusText}`);
              }
            }

            // Set `isExecuted` to true in IndexedDB
            await this._synchronizeApiExecutionStatusWithIndexedDb({
              vin: vin,
              userId: currentlyLoggedInUserId,
              indexOfArrayWhereApiCallIsExecuted: i,
              group: 'registration'
            });

            // update the UI state
            await this._updateUiState({ vin, group: 'registration' });

            break;
          }
          default: {
            console.log('None reached - 451237');
          }
        }
      }
    } else {
      console.log('registrationApiData is not an array');
    }

    this._toggleLoaderForGroup({ vin, group: 'registration' });
  }

  private _logFormData(fd: FormData) {
    const obj: Record<string, unknown> = {};

    for (let [key, value] of fd.entries()) {
      obj[key] = value instanceof File ? `File: ${value.name}` : value;
    }

    console.group('FormData');
    console.log(obj);
    console.groupEnd();
  }

  private async _handleInformationApiData(vin: string) {
    this._toggleLoaderForGroup({ vin, group: 'information' });
    const db = await this._getDatabase();
    const tx = db.transaction('VAM-PWA-API-CALLS', 'readwrite');
    const store = tx.objectStore('VAM-PWA-API-CALLS');
    const relevantDataAgainstVin = await store.get(vin);
    const currentlyLoggedInUserId = this._getCurrentlyLoggedInUserId();
    const relevantDataAgainstVinForThisUser = relevantDataAgainstVin?.[currentlyLoggedInUserId];
    await tx.done;

    const informationApiData = relevantDataAgainstVinForThisUser?.['apiCallsData']?.['information'];

    const token = this._authenticationService.userData.token;

    if (informationApiData !== null && Array.isArray(informationApiData)) {
      for (let i = 0; i < informationApiData.length; i++) {
        const d = informationApiData[i];

        const endpoint = d?.['apiData']?.['endpoint'];

        if (d?.['apiData']?.['isExecuted'] === true) {
          this._apiAlreadyExecutedLog({ endpoint, value: d });
          continue;
        }

        const url = `${environment.api}${d?.['apiData']?.['endpoint']}`;

        const map = new Map();
        for (let i = 0; i < d?.['keysToBeReplacedInApiRequestBody']?.length; i++) {
          const pair = d?.['keysToBeReplacedInApiRequestBody'][i];
          map.set(pair?.['remoteKey'], pair?.['localKey']);
        }

        const storedApiBody = d?.['apiData']?.['body'];

        let newApiBody = {};

        for (const [k, v] of Object.entries(storedApiBody)) {
          if (map.has(k)) {
            newApiBody = {
              ...newApiBody,
              [k]: relevantDataAgainstVinForThisUser?.['mapperData']?.[map.get(k)]?.['remote']
            };
          } else {
            newApiBody = {
              ...newApiBody,
              [k]: v
            };
          }
        }

        const contentType = d?.['apiData']?.['contentType'];

        const fd = new FormData();
        if (contentType === 'multipart/form-data') {
          console.log('it is a multipart/form-data');

          const keysWhereValuesAreBinaryFiles = d?.['apiData']?.[
            'keysWhereValuesAreBinaryFiles'
          ] as Set<string> | null;

          console.log('keysWhereValuesAreBinaryFiles: ', keysWhereValuesAreBinaryFiles);

          for (const [key, value] of Object.entries(newApiBody)) {
            if (typeof value === 'string') {
              if (keysWhereValuesAreBinaryFiles.has(key)) {
                const result = await this._getDataUrlFromStore(value);
                const fileDataUrl = result.dataUrl;
                const fileFormat = result.format;

                console.log('fileDataUrl: ', fileDataUrl);
                console.log('fileFormat: ', fileFormat);

                const file = await this.dataUrlToFile(fileDataUrl, `audio.${fileFormat}`);
                fd.append(key, file);
              } else {
                fd.append(key, value);
              }
            }
          }
        }

        let headers = {};

        headers['Authorization'] = `Bearer ${token}`;
        if (contentType !== 'multipart/form-data') {
          headers['Content-Type'] = d?.apiData?.['contentType'];
        }

        const res = await fetch(url, {
          body: contentType === 'application/json' ? JSON.stringify(newApiBody) : fd,
          method: d?.apiData?.['method'],
          headers
        });

        if (!res.ok) {
          throw new Error(`Error ${res.statusText}`);
        }

        await this._synchronizeApiExecutionStatusWithIndexedDb({
          vin: vin,
          userId: currentlyLoggedInUserId,
          indexOfArrayWhereApiCallIsExecuted: i,
          group: 'information'
        });

        await this._updateUiState({ vin, group: 'information' });
      }
    }
    this._toggleLoaderForGroup({ vin, group: 'information' });
  }

  private async _handleOptionsApiData(vin: string) {
    this._toggleLoaderForGroup({ vin, group: 'options' });

    const db = await this._getDatabase();
    const tx = db.transaction('VAM-PWA-API-CALLS', 'readwrite');
    const store = tx.objectStore('VAM-PWA-API-CALLS');
    const relevantDataAgainstVin = await store.get(vin);
    const currentlyLoggedInUserId = this._getCurrentlyLoggedInUserId();
    const relevantDataAgainstVinForThisUser = relevantDataAgainstVin?.[currentlyLoggedInUserId];
    await tx.done;

    const optionsApiData = relevantDataAgainstVinForThisUser?.['apiCallsData']?.['options'];

    const token = this._authenticationService.userData.token;

    if (optionsApiData !== null && Array.isArray(optionsApiData)) {
      for (let i = 0; i < optionsApiData.length; i++) {
        const d = optionsApiData[i];

        const endpoint = d?.['apiData']?.['endpoint'];

        if (d?.['apiData']?.['isExecuted'] === true) {
          this._apiAlreadyExecutedLog({ endpoint, value: d });
          continue;
        }

        const url = `${environment.api}${d?.['apiData']?.['endpoint']}`;

        const map = new Map();
        for (let i = 0; i < d?.['keysToBeReplacedInApiRequestBody']?.length; i++) {
          const pair = d?.['keysToBeReplacedInApiRequestBody'][i];
          map.set(pair?.['remoteKey'], pair?.['localKey']);
        }

        const storedApiBody = d?.['apiData']?.['body'];

        let newApiBody = {};

        for (const [k, v] of Object.entries(storedApiBody)) {
          if (map.has(k)) {
            newApiBody = {
              ...newApiBody,
              [k]: relevantDataAgainstVinForThisUser?.['mapperData']?.[map.get(k)]?.['remote']
            };
          } else {
            newApiBody = {
              ...newApiBody,
              [k]: v
            };
          }
        }

        const res = await fetch(url, {
          body: JSON.stringify(newApiBody),
          method: d?.apiData?.['method'],
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': d?.apiData?.['contentType']
          }
        });

        if (!res.ok) {
          throw new Error(`Error: ${res.statusText}`);
        }

        await this._synchronizeApiExecutionStatusWithIndexedDb({
          vin: vin,
          userId: currentlyLoggedInUserId,
          indexOfArrayWhereApiCallIsExecuted: i,
          group: 'options'
        });

        await this._updateUiState({ vin, group: 'options' });
      }
    }
    this._toggleLoaderForGroup({ vin, group: 'options' });
  }

  private async _handleFlagsApiData(vin: string) {
    this._toggleLoaderForGroup({ vin, group: 'flags' });

    const db = await this._getDatabase();
    const tx = db.transaction('VAM-PWA-API-CALLS', 'readwrite');
    const store = tx.objectStore('VAM-PWA-API-CALLS');
    const relevantDataAgainstVin = await store.get(vin);
    const currentlyLoggedInUserId = this._getCurrentlyLoggedInUserId();
    const relevantDataAgainstVinForThisUser = relevantDataAgainstVin?.[currentlyLoggedInUserId];
    await tx.done;

    const flagsApiData = relevantDataAgainstVinForThisUser?.['apiCallsData']?.['flags'];

    const token = this._authenticationService.userData.token;

    if (flagsApiData !== null && Array.isArray(flagsApiData)) {
      for (let i = 0; i < flagsApiData.length; i++) {
        const d = flagsApiData[i];

        const endpoint = d?.['apiData']?.['endpoint'];

        if (d?.['apiData']?.['isExecuted'] === true) {
          this._apiAlreadyExecutedLog({ endpoint, value: d });
          continue;
        }

        const url = `${environment.api}${d?.['apiData']?.['endpoint']}`;

        const map = new Map();
        for (let i = 0; i < d?.['keysToBeReplacedInApiRequestBody']?.length; i++) {
          const pair = d?.['keysToBeReplacedInApiRequestBody'][i];
          map.set(pair?.['remoteKey'], pair?.['localKey']);
        }

        const storedApiBody = d?.['apiData']?.['body'];

        let newApiBody = {};

        for (const [k, v] of Object.entries(storedApiBody)) {
          if (map.has(k)) {
            newApiBody = {
              ...newApiBody,
              [k]: relevantDataAgainstVinForThisUser?.['mapperData']?.[map.get(k)]?.['remote']
            };
          } else {
            newApiBody = {
              ...newApiBody,
              [k]: v
            };
          }
        }

        const res = await fetch(url, {
          body: JSON.stringify(newApiBody),
          method: d?.apiData?.['method'],
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': d?.apiData?.['contentType']
          }
        });

        if (!res.ok) {
          throw new Error(`Error: ${res.statusText}`);
        }

        await this._synchronizeApiExecutionStatusWithIndexedDb({
          vin: vin,
          userId: currentlyLoggedInUserId,
          indexOfArrayWhereApiCallIsExecuted: i,
          group: 'flags'
        });

        await this._updateUiState({ vin, group: 'flags' });
      }
    }

    this._toggleLoaderForGroup({ vin, group: 'flags' });
  }

  private async _handleRatingsApiData(vin: string) {
    this._toggleLoaderForGroup({ vin, group: 'ratings' });

    await this._handleRatingsExteriorData(vin);
    await this._handleRatingsInteriorData(vin);
    await this._handleRatingsFrameData(vin);
    await this._handleRatingsMechanicsData(vin);
    await this._handleRatingsMajorOptionsData(vin);
    await this._handleRatingsGenericData(vin);
    await this._handleRatingsGradingData(vin);

    this._toggleLoaderForGroup({ vin, group: 'ratings' });
  }

  private async _handleRatingsExteriorData(vin: string) {
    const db = await this._getDatabase();
    const tx = db.transaction('VAM-PWA-API-CALLS', 'readwrite');
    const store = tx.objectStore('VAM-PWA-API-CALLS');
    const relevantDataAgainstVin = await store.get(vin);
    const currentlyLoggedInUserId = this._getCurrentlyLoggedInUserId();
    const relevantDataAgainstVinForThisUser = relevantDataAgainstVin?.[currentlyLoggedInUserId];
    await tx.done;

    const ratingsApiData = relevantDataAgainstVinForThisUser?.['apiCallsData']?.['ratings'];

    const token = this._authenticationService.userData.token;
    const exteriorRatingsApiData = ratingsApiData?.['exterior'];

    let updatedObjectToIndicateApiCallHasBeenExecuted = {
      ...relevantDataAgainstVin
    };

    if (exteriorRatingsApiData !== null && Array.isArray(exteriorRatingsApiData)) {
      for (let i = 0; i < exteriorRatingsApiData.length; i++) {
        const d = exteriorRatingsApiData[i];

        // we need to check this first, because if the api has already been executed then we dont execute it again.
        if (d?.['apiData']?.['isExecuted'] === true) continue;

        const apiBodyData = d?.['apiData']?.['body'];
        const url = `${environment.api}${d?.['apiData']?.['endpoint']}`;

        const fd = new FormData();
        fd.append('parent_part_id', apiBodyData?.['parent_part_id']);
        fd.append('parts_conditions_id', apiBodyData?.['parts_conditions_id']);
        fd.append(
          'vehicle_id',
          relevantDataAgainstVinForThisUser?.['mapperData']?.['vehicleId']?.['remote']
        );
        fd.append('vpcs_lb_cost', apiBodyData?.['vpcs_lb_cost']);
        fd.append('vpcs_prt_cost', apiBodyData?.['vpcs_prt_cost']);
        fd.append('vpcs_rp_cost', apiBodyData?.['vpcs_rp_cost']);
        fd.append('vpcs_s_dec', apiBodyData?.['vpcs_s_dec']);
        fd.append('vpcs_severity', apiBodyData?.['vpcs_severity']);

        if (apiBodyData?.['vpcs_new_image1'] === '') {
          fd.append('vpcs_new_image1', '');
        } else {
          const vpcsNewImage1 = await this._getImageFromImageStore({
            imageId: apiBodyData?.['vpcs_new_image1'],
            get: 'image'
          });
          fd.append(
            'vpcs_new_image1',
            await this.dataUrlToFile(vpcsNewImage1, 'vpcs_new_image1.png')
          );
        }

        if (apiBodyData?.['vpcs_new_image2'] === '') {
          fd.append('vpcs_new_image2', '');
        } else {
          const vpcsNewImage1 = await this._getImageFromImageStore({
            imageId: apiBodyData?.['vpcs_new_image2'],
            get: 'image'
          });
          fd.append(
            'vpcs_new_image2',
            await this.dataUrlToFile(vpcsNewImage1, 'vpcs_new_image2.png')
          );
        }

        if (apiBodyData?.['vpcs_old_image1'] === '') {
          fd.append('vpcs_old_image1', '');
        } else {
          const vpcsNewImage1 = await this._getImageFromImageStore({
            imageId: apiBodyData?.['vpcs_old_image1'],
            get: 'image'
          });
          fd.append(
            'vpcs_old_image1',
            await this.dataUrlToFile(vpcsNewImage1, 'vpcs_old_image1.png')
          );
        }

        if (apiBodyData?.['vpcs_old_image2'] === '') {
          fd.append('vpcs_old_image2', '');
        } else {
          const vpcsNewImage1 = await this._getImageFromImageStore({
            imageId: apiBodyData?.['vpcs_old_image2'],
            get: 'image'
          });
          fd.append(
            'vpcs_old_image2',
            await this.dataUrlToFile(vpcsNewImage1, 'vpcs_old_image2.png')
          );
        }

        const res = await fetch(url, {
          body: fd,
          method: d?.['apiData']?.['method'],
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        if (!res.ok) {
          throw new Error(`Error: ${res.statusText}`);
        }

        await this._synchronizeApiExecutionStatusWithIndexedDb({
          vin: vin,
          userId: currentlyLoggedInUserId,
          indexOfArrayWhereApiCallIsExecuted: i,
          group: 'ratings',
          subgroup: 'exterior'
        });

        await this._updateUiState({ vin, group: 'ratings' });
      }
    }
  }

  private async _handleRatingsInteriorData(vin: string) {
    const db = await this._getDatabase();
    const tx = db.transaction('VAM-PWA-API-CALLS', 'readwrite');
    const store = tx.objectStore('VAM-PWA-API-CALLS');
    const relevantDataAgainstVin = await store.get(vin);
    const currentlyLoggedInUserId = this._getCurrentlyLoggedInUserId();
    const relevantDataAgainstVinForThisUser = relevantDataAgainstVin?.[currentlyLoggedInUserId];
    await tx.done;

    const ratingsApiData = relevantDataAgainstVinForThisUser?.['apiCallsData']?.['ratings'];

    const token = this._authenticationService.userData.token;

    const interiorRatingsApiData = ratingsApiData?.['interior'];

    let updatedObjectToIndicateApiCallHasBeenExecuted = {
      ...relevantDataAgainstVin
    };

    if (interiorRatingsApiData !== null && Array.isArray(interiorRatingsApiData)) {
      for (let i = 0; i < interiorRatingsApiData.length; i++) {
        const d = interiorRatingsApiData[i];

        // we need to check this first, because if the api has already been executed then we dont execute it again.
        if (d?.['apiData']?.['isExecuted'] === true) continue;

        const apiBodyData = d?.['apiData']?.['body'];
        const url = `${environment.api}${d?.['apiData']?.['endpoint']}`;

        const fd = new FormData();
        fd.append('parent_part_id', apiBodyData?.['parent_part_id']);
        fd.append('parts_conditions_id', apiBodyData?.['parts_conditions_id']);
        fd.append(
          'vehicle_id',
          relevantDataAgainstVinForThisUser?.['mapperData']?.['vehicleId']?.['remote']
        );
        fd.append('vpcs_lb_cost', apiBodyData?.['vpcs_lb_cost']);
        fd.append('vpcs_prt_cost', apiBodyData?.['vpcs_prt_cost']);
        fd.append('vpcs_rp_cost', apiBodyData?.['vpcs_rp_cost']);
        fd.append('vpcs_s_dec', apiBodyData?.['vpcs_s_dec']);
        fd.append('vpcs_severity', apiBodyData?.['vpcs_severity']);

        if (apiBodyData?.['vpcs_new_image1'] === '') {
          fd.append('vpcs_new_image1', '');
        } else {
          const vpcsNewImage1 = await this._getImageFromImageStore({
            imageId: apiBodyData?.['vpcs_new_image1'],
            get: 'image'
          });
          const file = await this.dataUrlToFile(vpcsNewImage1, 'vpcs_new_image1.png');
          fd.append('vpcs_new_image1', file);
        }

        if (apiBodyData?.['vpcs_new_image2'] === '') {
          fd.append('vpcs_new_image2', '');
        } else {
          const vpcsNewImage1 = await this._getImageFromImageStore({
            imageId: apiBodyData?.['vpcs_new_image2'],
            get: 'image'
          });
          const file = await this.dataUrlToFile(vpcsNewImage1, 'vpcs_new_image2.png');
          fd.append('vpcs_new_image2', file);
        }

        if (apiBodyData?.['vpcs_old_image1'] === '') {
          fd.append('vpcs_old_image1', '');
        } else {
          const vpcsNewImage1 = await this._getImageFromImageStore({
            imageId: apiBodyData?.['vpcs_old_image1'],
            get: 'image'
          });
          const file = await this.dataUrlToFile(vpcsNewImage1, 'vpcs_old_image1.png');
          fd.append('vpcs_old_image1', file);
        }

        if (apiBodyData?.['vpcs_old_image2'] === '') {
          fd.append('vpcs_old_image2', '');
        } else {
          const vpcsNewImage1 = await this._getImageFromImageStore({
            imageId: apiBodyData?.['vpcs_old_image2'],
            get: 'image'
          });
          const file = await this.dataUrlToFile(vpcsNewImage1, 'vpcs_old_image2.png');
          fd.append('vpcs_old_image2', file);
        }

        const res = await fetch(url, {
          body: fd,
          method: d?.['apiData']?.['method'],
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        if (!res.ok) {
          throw new Error(`Error: ${res.statusText}`);
        }

        await this._synchronizeApiExecutionStatusWithIndexedDb({
          vin: vin,
          userId: currentlyLoggedInUserId,
          indexOfArrayWhereApiCallIsExecuted: i,
          group: 'ratings',
          subgroup: 'interior'
        });

        await this._updateUiState({ vin, group: 'ratings' });
      }
    }
  }

  private async _handleRatingsFrameData(vin: string) {
    const db = await this._getDatabase();
    const tx = db.transaction('VAM-PWA-API-CALLS', 'readwrite');
    const store = tx.objectStore('VAM-PWA-API-CALLS');
    const relevantDataAgainstVin = await store.get(vin);
    const currentlyLoggedInUserId = this._getCurrentlyLoggedInUserId();
    const relevantDataAgainstVinForThisUser = relevantDataAgainstVin?.[currentlyLoggedInUserId];
    await tx.done;

    const ratingsApiData = relevantDataAgainstVinForThisUser?.['apiCallsData']?.['ratings'];

    const token = this._authenticationService.userData.token;

    const frameRatingsApiData = ratingsApiData?.['frame'];

    let updatedObjectToIndicateApiCallHasBeenExecuted = {
      ...relevantDataAgainstVin
    };

    if (frameRatingsApiData !== null && Array.isArray(frameRatingsApiData)) {
      for (let i = 0; i < frameRatingsApiData.length; i++) {
        const d = frameRatingsApiData[i];

        // we need to check this first, because if the api has already been executed then we dont execute it again.
        if (d?.['apiData']?.['isExecuted'] === true) continue;

        const apiBodyData = d?.['apiData']?.['body'];
        const url = `${environment.api}${d?.['apiData']?.['endpoint']}`;

        const fd = new FormData();
        fd.append('parent_part_id', apiBodyData?.['parent_part_id']);
        fd.append('parts_conditions_id', apiBodyData?.['parts_conditions_id']);
        fd.append(
          'vehicle_id',
          relevantDataAgainstVinForThisUser?.['mapperData']?.['vehicleId']?.['remote']
        );
        fd.append('vpcs_lb_cost', apiBodyData?.['vpcs_lb_cost']);
        fd.append('vpcs_prt_cost', apiBodyData?.['vpcs_prt_cost']);
        fd.append('vpcs_rp_cost', apiBodyData?.['vpcs_rp_cost']);
        fd.append('vpcs_s_dec', apiBodyData?.['vpcs_s_dec']);
        fd.append('vpcs_severity', apiBodyData?.['vpcs_severity']);
        if (apiBodyData?.['vpcs_new_image1'] === '') {
          fd.append('vpcs_new_image1', '');
        } else {
          const vpcsNewImage1 = await this._getImageFromImageStore({
            imageId: apiBodyData?.['vpcs_new_image1'],
            get: 'image'
          });
          const file = await this.dataUrlToFile(vpcsNewImage1, 'vpcs_new_image1.png');
          fd.append('vpcs_new_image1', file);
        }

        if (apiBodyData?.['vpcs_new_image2'] === '') {
          fd.append('vpcs_new_image2', '');
        } else {
          const vpcsNewImage1 = await this._getImageFromImageStore({
            imageId: apiBodyData?.['vpcs_new_image2'],
            get: 'image'
          });
          const file = await this.dataUrlToFile(vpcsNewImage1, 'vpcs_new_image2.png');
          fd.append('vpcs_new_image2', file);
        }

        if (apiBodyData?.['vpcs_old_image1'] === '') {
          fd.append('vpcs_old_image1', '');
        } else {
          const vpcsNewImage1 = await this._getImageFromImageStore({
            imageId: apiBodyData?.['vpcs_old_image1'],
            get: 'image'
          });
          const file = await this.dataUrlToFile(vpcsNewImage1, 'vpcs_old_image1.png');
          fd.append('vpcs_old_image1', file);
        }

        if (apiBodyData?.['vpcs_old_image2'] === '') {
          fd.append('vpcs_old_image2', '');
        } else {
          const vpcsNewImage1 = await this._getImageFromImageStore({
            imageId: apiBodyData?.['vpcs_old_image2'],
            get: 'image'
          });
          const file = await this.dataUrlToFile(vpcsNewImage1, 'vpcs_old_image2.png');
          fd.append('vpcs_old_image2', file);
        }

        const res = await fetch(url, {
          body: fd,
          method: d?.['apiData']?.['method'],
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        if (!res.ok) {
          throw new Error(`Error: ${res.statusText}`);
        }

        await this._synchronizeApiExecutionStatusWithIndexedDb({
          vin: vin,
          userId: currentlyLoggedInUserId,
          indexOfArrayWhereApiCallIsExecuted: i,
          group: 'ratings',
          subgroup: 'frame'
        });

        await this._updateUiState({ vin, group: 'ratings' });
      }
    }
  }

  private async _handleRatingsMechanicsData(vin: string) {
    const db = await this._getDatabase();
    const tx = db.transaction('VAM-PWA-API-CALLS', 'readwrite');
    const store = tx.objectStore('VAM-PWA-API-CALLS');
    const relevantDataAgainstVin = await store.get(vin);
    const currentlyLoggedInUserId = this._getCurrentlyLoggedInUserId();
    const relevantDataAgainstVinForThisUser = relevantDataAgainstVin?.[currentlyLoggedInUserId];
    await tx.done;

    const ratingsApiData = relevantDataAgainstVinForThisUser?.['apiCallsData']?.['ratings'];

    const token = this._authenticationService.userData.token;

    const mechanicsRatingsApiData = ratingsApiData?.['mechanics'];

    let updatedObjectToIndicateApiCallHasBeenExecuted = {
      ...relevantDataAgainstVin
    };

    if (mechanicsRatingsApiData !== null && Array.isArray(mechanicsRatingsApiData)) {
      for (let i = 0; i < mechanicsRatingsApiData.length; i++) {
        const d = mechanicsRatingsApiData[i];

        // we need to check this first, because if the api has already been executed then we dont execute it again.
        if (d?.['apiData']?.['isExecuted'] === true) continue;

        const apiBodyData = d?.['apiData']?.['body'];
        const url = `${environment.api}${d?.['apiData']?.['endpoint']}`;

        const fd = new FormData();
        fd.append('parent_part_id', apiBodyData?.['parent_part_id']);
        fd.append('parts_conditions_id', apiBodyData?.['parts_conditions_id']);
        fd.append(
          'vehicle_id',
          relevantDataAgainstVinForThisUser?.['mapperData']?.['vehicleId']?.['remote']
        );
        fd.append('vpcs_lb_cost', apiBodyData?.['vpcs_lb_cost']);
        fd.append('vpcs_prt_cost', apiBodyData?.['vpcs_prt_cost']);
        fd.append('vpcs_rp_cost', apiBodyData?.['vpcs_rp_cost']);
        fd.append('vpcs_s_dec', apiBodyData?.['vpcs_s_dec']);
        fd.append('vpcs_severity', apiBodyData?.['vpcs_severity']);
        if (apiBodyData?.['vpcs_new_image1'] === '') {
          fd.append('vpcs_new_image1', '');
        } else {
          const vpcsNewImage1 = await this._getImageFromImageStore({
            imageId: apiBodyData?.['vpcs_new_image1'],
            get: 'image'
          });
          const file = await this.dataUrlToFile(vpcsNewImage1, 'vpcs_new_image1.png');
          fd.append('vpcs_new_image1', file);
        }

        if (apiBodyData?.['vpcs_new_image2'] === '') {
          fd.append('vpcs_new_image2', '');
        } else {
          const vpcsNewImage1 = await this._getImageFromImageStore({
            imageId: apiBodyData?.['vpcs_new_image2'],
            get: 'image'
          });
          const file = await this.dataUrlToFile(vpcsNewImage1, 'vpcs_new_image2.png');
          fd.append('vpcs_new_image2', file);
        }

        if (apiBodyData?.['vpcs_old_image1'] === '') {
          fd.append('vpcs_old_image1', '');
        } else {
          const vpcsNewImage1 = await this._getImageFromImageStore({
            imageId: apiBodyData?.['vpcs_old_image1'],
            get: 'image'
          });
          const file = await this.dataUrlToFile(vpcsNewImage1, 'vpcs_old_image1.png');
          fd.append('vpcs_old_image1', file);
        }

        if (apiBodyData?.['vpcs_old_image2'] === '') {
          fd.append('vpcs_old_image2', '');
        } else {
          const vpcsNewImage1 = await this._getImageFromImageStore({
            imageId: apiBodyData?.['vpcs_old_image2'],
            get: 'image'
          });
          const file = await this.dataUrlToFile(vpcsNewImage1, 'vpcs_old_image2.png');
          fd.append('vpcs_old_image2', file);
        }

        const res = await fetch(url, {
          body: fd,
          method: d?.['apiData']?.['method'],
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        if (!res.ok) {
          throw new Error(`Error: ${res.statusText}`);
        }

        await this._synchronizeApiExecutionStatusWithIndexedDb({
          vin: vin,
          userId: currentlyLoggedInUserId,
          indexOfArrayWhereApiCallIsExecuted: i,
          group: 'ratings',
          subgroup: 'mechanics'
        });

        await this._updateUiState({ vin, group: 'ratings' });
      }
    }
  }

  private async _handleRatingsMajorOptionsData(vin: string) {
    const db = await this._getDatabase();
    const tx = db.transaction('VAM-PWA-API-CALLS', 'readwrite');
    const store = tx.objectStore('VAM-PWA-API-CALLS');
    const relevantDataAgainstVin = await store.get(vin);
    const currentlyLoggedInUserId = this._getCurrentlyLoggedInUserId();
    const relevantDataAgainstVinForThisUser = relevantDataAgainstVin?.[currentlyLoggedInUserId];
    await tx.done;

    const ratingsApiData = relevantDataAgainstVinForThisUser?.['apiCallsData']?.['ratings'];

    const token = this._authenticationService.userData.token;

    const majorOptionsRatingsApiData = ratingsApiData?.['major_options'];

    let updatedObjectToIndicateApiCallHasBeenExecuted = {
      ...relevantDataAgainstVin
    };

    if (majorOptionsRatingsApiData !== null && Array.isArray(majorOptionsRatingsApiData)) {
      for (let i = 0; i < majorOptionsRatingsApiData.length; i++) {
        const d = majorOptionsRatingsApiData[i];

        // we need to check this first, because if the api has already been executed then we dont execute it again.
        if (d?.['apiData']?.['isExecuted'] === true) continue;

        const apiBodyData = d?.['apiData']?.['body'];
        const url = `${environment.api}${d?.['apiData']?.['endpoint']}`;

        const fd = new FormData();
        fd.append('parent_part_id', apiBodyData?.['parent_part_id']);
        fd.append('parts_conditions_id', apiBodyData?.['parts_conditions_id']);
        fd.append(
          'vehicle_id',
          relevantDataAgainstVinForThisUser?.['mapperData']?.['vehicleId']?.['remote']
        );
        fd.append('vpcs_lb_cost', apiBodyData?.['vpcs_lb_cost']);
        fd.append('vpcs_prt_cost', apiBodyData?.['vpcs_prt_cost']);
        fd.append('vpcs_rp_cost', apiBodyData?.['vpcs_rp_cost']);
        fd.append('vpcs_s_dec', apiBodyData?.['vpcs_s_dec']);
        fd.append('vpcs_severity', apiBodyData?.['vpcs_severity']);
        if (apiBodyData?.['vpcs_new_image1'] === '') {
          fd.append('vpcs_new_image1', '');
        } else {
          const vpcsNewImage1 = await this._getImageFromImageStore({
            imageId: apiBodyData?.['vpcs_new_image1'],
            get: 'image'
          });
          const file = await this.dataUrlToFile(vpcsNewImage1, 'vpcs_new_image1.png');
          fd.append('vpcs_new_image1', file);
        }

        if (apiBodyData?.['vpcs_new_image2'] === '') {
          fd.append('vpcs_new_image2', '');
        } else {
          const vpcsNewImage1 = await this._getImageFromImageStore({
            imageId: apiBodyData?.['vpcs_new_image2'],
            get: 'image'
          });
          const file = await this.dataUrlToFile(vpcsNewImage1, 'vpcs_new_image2.png');
          fd.append('vpcs_new_image2', file);
        }

        if (apiBodyData?.['vpcs_old_image1'] === '') {
          fd.append('vpcs_old_image1', '');
        } else {
          const vpcsNewImage1 = await this._getImageFromImageStore({
            imageId: apiBodyData?.['vpcs_old_image1'],
            get: 'image'
          });
          const file = await this.dataUrlToFile(vpcsNewImage1, 'vpcs_old_image1.png');
          fd.append('vpcs_old_image1', file);
        }

        if (apiBodyData?.['vpcs_old_image2'] === '') {
          fd.append('vpcs_old_image2', '');
        } else {
          const vpcsNewImage1 = await this._getImageFromImageStore({
            imageId: apiBodyData?.['vpcs_old_image2'],
            get: 'image'
          });
          const file = await this.dataUrlToFile(vpcsNewImage1, 'vpcs_old_image2.png');
          fd.append('vpcs_old_image2', file);
        }

        const res = await fetch(url, {
          body: fd,
          method: d?.['apiData']?.['method'],
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        if (!res.ok) {
          throw new Error(`Error: ${res.statusText}`);
        }

        await this._synchronizeApiExecutionStatusWithIndexedDb({
          vin: vin,
          userId: currentlyLoggedInUserId,
          indexOfArrayWhereApiCallIsExecuted: i,
          group: 'ratings',
          subgroup: 'major_options'
        });

        await this._updateUiState({ vin, group: 'ratings' });
      }
    }
  }

  private async _handleRatingsGenericData(vin: string) {
    const db = await this._getDatabase();
    const tx = db.transaction('VAM-PWA-API-CALLS', 'readwrite');
    const store = tx.objectStore('VAM-PWA-API-CALLS');
    const relevantDataAgainstVin = await store.get(vin);
    const currentlyLoggedInUserId = this._getCurrentlyLoggedInUserId();
    const relevantDataAgainstVinForThisUser = relevantDataAgainstVin?.[currentlyLoggedInUserId];
    await tx.done;

    const ratingsApiData = relevantDataAgainstVinForThisUser?.['apiCallsData']?.['ratings'];

    const token = this._authenticationService.userData.token;

    const genericRatingsApiData = ratingsApiData?.['generic'];

    let updatedObjectToIndicateApiCallHasBeenExecuted = {
      ...relevantDataAgainstVin
    };

    if (genericRatingsApiData !== null && Array.isArray(genericRatingsApiData)) {
      for (let i = 0; i < genericRatingsApiData.length; i++) {
        const d = genericRatingsApiData[i];

        // we need to check this first, because if the api has already been executed then we dont execute it again.
        if (d?.['apiData']?.['isExecuted'] === true) continue;

        const apiBodyData = d?.['apiData']?.['body'];
        const url = `${environment.api}${d?.['apiData']?.['endpoint']}`;

        const body = {
          rating_states: apiBodyData?.['rating_states'],
          rating_states_id: apiBodyData?.['rating_states_id'],
          vehicle_id: relevantDataAgainstVinForThisUser?.['mapperData']?.['vehicleId']?.['remote']
        };

        const res = await fetch(url, {
          body: JSON.stringify(body),
          method: d?.['apiData']?.['method'],
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!res.ok) {
          throw new Error(`Error: ${res.statusText}`);
        }

        await this._synchronizeApiExecutionStatusWithIndexedDb({
          vin: vin,
          userId: currentlyLoggedInUserId,
          indexOfArrayWhereApiCallIsExecuted: i,
          group: 'ratings',
          subgroup: 'generic'
        });

        await this._updateUiState({ vin, group: 'ratings' });
      }
    }
  }

  private async _handleRatingsGradingData(vin: string) {
    const db = await this._getDatabase();
    const tx = db.transaction('VAM-PWA-API-CALLS', 'readwrite');
    const store = tx.objectStore('VAM-PWA-API-CALLS');
    const relevantDataAgainstVin = await store.get(vin);
    const currentlyLoggedInUserId = this._getCurrentlyLoggedInUserId();
    const relevantDataAgainstVinForThisUser = relevantDataAgainstVin?.[currentlyLoggedInUserId];
    await tx.done;

    const ratingsApiData = relevantDataAgainstVinForThisUser?.['apiCallsData']?.['ratings'];

    const token = this._authenticationService.userData.token;

    const majorOptionsRatingsApiData = ratingsApiData?.['grading'];

    let updatedObjectToIndicateApiCallHasBeenExecuted = {
      ...relevantDataAgainstVin
    };

    if (majorOptionsRatingsApiData !== null && Array.isArray(majorOptionsRatingsApiData)) {
      for (let i = 0; i < majorOptionsRatingsApiData.length; i++) {
        const d = majorOptionsRatingsApiData[i];

        // we need to check this first, because if the api has already been executed then we dont execute it again.
        if (d?.['apiData']?.['isExecuted'] === true) continue;

        const apiBodyData = d?.['apiData']?.['body'];
        const url = `${environment.api}${d?.['apiData']?.['endpoint']}`;

        const res = await fetch(url, {
          body: JSON.stringify({
            vehicle_id:
              relevantDataAgainstVinForThisUser?.['mapperData']?.['vehicleId']?.['remote'],
            overall_exterior: apiBodyData?.['overall_exterior'],
            overall_interior: apiBodyData?.['overall_interior'],
            overall_mechanics: apiBodyData?.['overall_mechanics'],
            overall_frame: apiBodyData?.['overall_frame'],
            overall_major_options: apiBodyData?.['overall_major_options'],
            calculated_value: apiBodyData?.['calculated_value'],
            perceived_value: apiBodyData?.['perceived_value'],
            overall_repair_cost: apiBodyData?.['overall_repair_cost']
          }),
          method: d?.['apiData']?.['method'],
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': d?.apiData?.['contentType']
          }
        });

        if (!res.ok) {
          throw new Error(`Error: ${res.statusText}`);
        }

        await this._synchronizeApiExecutionStatusWithIndexedDb({
          vin: vin,
          userId: currentlyLoggedInUserId,
          indexOfArrayWhereApiCallIsExecuted: i,
          group: 'ratings',
          subgroup: 'grading'
        });

        await this._updateUiState({ vin, group: 'ratings' });
      }
    }
  }

  private async _handleGalleryApiData(vin: string) {
    this._toggleLoaderForGroup({ vin, group: 'gallery' });

    const db = await this._getDatabase();
    const tx = db.transaction('VAM-PWA-API-CALLS', 'readwrite');
    const store = tx.objectStore('VAM-PWA-API-CALLS');
    const relevantDataAgainstVin = await store.get(vin);
    const currentlyLoggedInUserId = this._getCurrentlyLoggedInUserId();
    const relevantDataAgainstVinForThisUser = relevantDataAgainstVin?.[currentlyLoggedInUserId];
    await tx.done;

    const galleryApiData = relevantDataAgainstVinForThisUser?.['apiCallsData']?.['gallery'];

    const token = this._authenticationService.userData.token;

    let updatedObjectToIndicateApiCallHasBeenExecuted = {
      ...relevantDataAgainstVin
    };

    if (galleryApiData !== null && Array.isArray(galleryApiData)) {
      for (let i = 0; i < galleryApiData.length; i++) {
        const d = galleryApiData[i];

        const endpoint = d?.['apiData']?.['endpoint'];

        if (d?.['apiData']?.['isExecuted'] === true) {
          this._apiAlreadyExecutedLog({ endpoint, value: d });
          continue;
        }

        const url = `${environment.api}${d?.['apiData']?.['endpoint']}`;

        const map = new Map();
        for (let i = 0; i < d?.['keysToBeReplacedInApiRequestBody']?.length; i++) {
          const pair = d?.['keysToBeReplacedInApiRequestBody'][i];
          map.set(pair?.['remoteKey'], pair?.['localKey']);
        }

        const storedApiBody = d?.['apiData']?.['body'];

        let newApiBody = {};

        const fd = new FormData();
        fd.append('part_id', storedApiBody?.['part_id']);
        fd.append('part_name', storedApiBody?.['part_name']);
        fd.append(
          'vehicle_id',
          relevantDataAgainstVinForThisUser?.['mapperData']?.['vehicleId']?.['remote']
        );

        if (storedApiBody?.['vehicle_gallery_data'] === '') {
          fd.append('vehicle_gallery_data', '');
        } else {
          const vehicleGalleryImageDataUrl = await this._getImageFromImageStore({
            imageId: storedApiBody?.['vehicle_gallery_data'],
            get: 'image'
          });
          const vehicleGalleryImageFile = await this.dataUrlToFile(
            vehicleGalleryImageDataUrl,
            `${storedApiBody?.['part_name']}.png`
          );
          fd.append('vehicle_gallery_data', vehicleGalleryImageFile);
        }

        // fd.append(
        //   'vehicle_gallery_data',
        //   storedApiBody?.['vehicle_gallery_data'] === ''
        //     ? ''
        //     : await this.dataUrlToFile(storedApiBody?.['vehicle_gallery_data'], 'file.png')
        // );

        const res = await fetch(url, {
          body: fd,
          method: d?.apiData?.['method'],
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        if (!res.ok) {
          throw new Error(`Error: ${res.statusText}`);
        }

        this._synchronizeApiExecutionStatusWithIndexedDb({
          group: 'gallery',
          userId: currentlyLoggedInUserId,
          vin: vin,
          indexOfArrayWhereApiCallIsExecuted: i
        });

        await this._updateUiState({ vin, group: 'gallery' });
      }
    }

    this._toggleLoaderForGroup({ vin, group: 'gallery' });
  }

  private _getCurrentlyLoggedInUserId() {
    const userDataStoredInLocalStorage = localStorage.getItem('user');
    const parsedUserData = JSON.parse(userDataStoredInLocalStorage);
    const userId = parsedUserData?.['user']?.['user_data']?.['id'] ?? -1;
    return userId;
  }

  public async dataUrlToFile(dataUrl: string, fileName: string) {
    // Split the Data URL to get MIME type and Base64 data
    const [meta, base64Data] = dataUrl.split(',');
    const mimeMatch = meta.match(/:(.*?);/);
    const mimeType = mimeMatch ? mimeMatch[1] : 'application/octet-stream'; // Default fallback MIME type

    // Convert Base64 to a binary Uint8Array
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);

    // Create a Blob from the binary data
    const blob = new Blob([byteArray], { type: mimeType });

    // Convert Blob to File (optional)
    const file = new File([blob], fileName, { type: mimeType });
    return file;
  }

  public async getWhatPercentageOfApiCallHasBeenMadeUnderGroup(args: {
    group: 'registration' | 'information' | 'options' | 'flags' | 'ratings' | 'gallery';
    vin: string;
  }) {
    const group = args.group;
    const vin = args.vin;
    const db = await this._getDatabase();
    const tx = db.transaction('VAM-PWA-API-CALLS', 'readonly');
    const store = tx.objectStore('VAM-PWA-API-CALLS');
    const relevantDataAgainstVin = await store.get(vin);
    const currentlyLoggedInUserId = this._getCurrentlyLoggedInUserId();
    const relevantDataAgainstVinForThisUser = relevantDataAgainstVin?.[currentlyLoggedInUserId];
    await tx.done;

    let totalApiCallCount = 0;
    let finishedApiCallCount = 0;

    // since `ratings` will be nested object and not an array so we need to handle that case separately.
    if (group === 'ratings') {
      const ratingsSpecificApiCallData =
        relevantDataAgainstVinForThisUser?.['apiCallsData']?.[group];
      if (ratingsSpecificApiCallData !== null) {
        for (const [k, v] of Object.entries(ratingsSpecificApiCallData)) {
          if (v !== null && Array.isArray(v)) {
            totalApiCallCount += v.length;
            for (let i = 0; i < v.length; i++) {
              if (v[i]?.['apiData']?.['isExecuted'] === true) {
                finishedApiCallCount++;
              }
            }
          }
        }
      }
    } else {
      const groupSpecificApiCallData = relevantDataAgainstVinForThisUser?.['apiCallsData']?.[group];
      if (Array.isArray(groupSpecificApiCallData)) {
        totalApiCallCount = groupSpecificApiCallData.length;
        for (let i = 0; i < groupSpecificApiCallData.length; i++) {
          const d = groupSpecificApiCallData[i];
          if (d?.['apiData']?.['isExecuted'] === true) {
            finishedApiCallCount++;
          }
        }
      }
    }

    return (finishedApiCallCount / totalApiCallCount) * 100;
  }
}
